/**
 * Authentication context.
 *
 * On every app boot we attempt a "silent refresh": call
 * /auth/token/refresh/ with no body — the browser supplies the
 * httpOnly refresh-token cookie automatically. If it returns 200 we
 * re-hydrate the access token and the user is back where they left off
 * (this is what makes a page reload no longer kick them to /login).
 *
 * `bootstrapping` is exposed so route guards can hold rendering for
 * the first refresh attempt and avoid a flicker through /login.
 */

import { createContext, useCallback, useContext, useEffect, useState } from "react";

import {
  clearAccessToken,
  getAccessToken,
  onAuthLost,
  refreshAccessToken,
  setAccessToken,
} from "../api/client";
import { auth as authApi } from "../api/endpoints";

const AuthContext = createContext(null);

export function AuthProvider({ children }) {
  const [isAuthenticated, setIsAuthenticated] = useState(() => Boolean(getAccessToken()));
  const [bootstrapping, setBootstrapping] = useState(true);

  useEffect(() => {
    onAuthLost(() => setIsAuthenticated(false));
  }, []);

  // Boot-time silent refresh — tolerate a 401 (just means no session).
  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        await refreshAccessToken();
        if (!cancelled) setIsAuthenticated(true);
      } catch {
        if (!cancelled) setIsAuthenticated(false);
      } finally {
        if (!cancelled) setBootstrapping(false);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, []);

  const login = useCallback(async (email, password) => {
    const { data } = await authApi.login({ email, password });
    setAccessToken(data.access);
    setIsAuthenticated(true);
  }, []);

  const register = useCallback(async (payload) => {
    const { data } = await authApi.register(payload);
    setAccessToken(data.access);
    setIsAuthenticated(true);
  }, []);

  const logout = useCallback(() => {
    // Clear local state SYNCHRONOUSLY so route guards re-render
    // immediately and don't race with the API call. The server-side
    // cookie clear is fire-and-forget — worst case the cookie sits
    // until its 14-day TTL expires, which still cannot get past
    // missing local access tokens.
    clearAccessToken();
    setIsAuthenticated(false);
    authApi.logout().catch(() => {});
  }, []);

  return (
    <AuthContext.Provider
      value={{ isAuthenticated, bootstrapping, login, register, logout }}
    >
      {children}
    </AuthContext.Provider>
  );
}

export function useAuth() {
  const ctx = useContext(AuthContext);
  if (!ctx) throw new Error("useAuth must be used inside <AuthProvider>");
  return ctx;
}
